import React from 'react';
import PropTypes from 'prop-types';
import tracking from 'dibs-tracking';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';
import { getNumberOfTiles } from '../../utils/adHelpers';
import { createFragmentContainer, graphql } from 'react-relay';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';
import {
    isLatestPage,
    getSelectedValue,
    setSelectedValue,
    getPageSizeSelectOptions,
} from './sbRespPageSizeHelpers';

// components
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';

// styles
import styles from './SbRespPageSize.scss';

function trackPageSizeEvent(pageSizeValue) {
    tracking.trackEvent({
        category: 'results page refinement',
        action: 'results displayed',
        label: `${pageSizeValue} results clicked`,
        value: 0,
    });
}

function PageSizeLinks({ options, onChange, pageSize, isSbAdsEnabled }) {
    return options.map(({ value }) => {
        const isSelected = getSelectedValue(pageSize) === value;
        return (
            <span
                key={value}
                className={classNames(styles.link, { [styles.selected]: isSelected })}
                onClick={() => {
                    if (!isSelected) {
                        onChange({ target: { value } }, isSbAdsEnabled);
                    }
                }}
            >
                {value}
            </span>
        );
    });
}

export function SbRespPageSizeComponent({
    onChange,
    pageSize,
    itemSearch,
    intl,
    usePageSizeLinks,
}) {
    const { appliedFilters } = itemSearch;
    const options = getPageSizeSelectOptions(intl);

    if (isLatestPage(appliedFilters)) {
        return null;
    }

    return (
        <ServerVarsContextConsumer>
            {({ isSbAdsEnabled }) => {
                return usePageSizeLinks ? (
                    <PageSizeLinks
                        pageSize={pageSize}
                        onChange={onChange}
                        options={options}
                        isSbAdsEnabled={isSbAdsEnabled}
                    />
                ) : (
                    <div className={styles.container}>
                        <BasicSelect
                            size="medium"
                            options={options}
                            onChange={e => onChange(e, isSbAdsEnabled)}
                            dataTn="sb-resp-page-size"
                            value={getSelectedValue(pageSize)}
                        />
                    </div>
                );
            }}
        </ServerVarsContextConsumer>
    );
}

SbRespPageSizeComponent.defaultProps = {
    onChange: () => {},
};

SbRespPageSizeComponent.propTypes = {
    intl: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    pageSize: PropTypes.number,
    itemSearch: PropTypes.object.isRequired,
    usePageSizeLinks: PropTypes.bool,
};

const mapStateToProps = state => {
    const { relayVariables } = state;
    const {
        variables: { first },
    } = relayVariables;
    return {
        pageSize: first,
    };
};

function mapDispatchToProps(dispatch, props) {
    return {
        onChange(e, isSbAdsEnabled) {
            const { itemSearch } = props;
            const { displayUriRef } = itemSearch;

            // set page size in cookies
            setSelectedValue(e.target.value);

            // track event in GA
            trackPageSizeEvent(e.target.value);

            // update relay variables
            dispatch(
                updateRelayVariables({
                    first: getNumberOfTiles({
                        pageSize: parseInt(e.target.value, 10),
                        path: displayUriRef,
                        isSbAdsEnabled,
                    }),
                })
            );
        },
    };
}

export const SbRespPageSize = createFragmentContainer(
    injectIntl(connect(mapStateToProps, mapDispatchToProps)(SbRespPageSizeComponent)),
    {
        itemSearch: graphql`
            fragment SbRespPageSize_itemSearch on ItemSearchQueryConnection {
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
                displayUriRef
            }
        `,
    }
);
